import { useEffect, useState } from "react";
import { Loader2, Users, UserCheck, Briefcase, Zap, Image as ImageIcon, Mail, Store, FileText, MessageSquare, ShieldCheck } from "lucide-react";
import api from "../lib/apiClient";

function Stat({ label, value, icon: Icon, testid }) {
  return (
    <div className="bg-[#18181E] border border-zinc-800 rounded-2xl p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-zinc-500 text-xs mb-2"><Icon className="w-4 h-4" /> {label}</div>
      <p className="text-2xl font-extrabold">{value}</p>
    </div>
  );
}

function Breakdown({ title, data, testid }) {
  const entries = Object.entries(data || {}).sort((a, b) => b[1] - a[1]);
  return (
    <div className="bg-[#18181E] border border-zinc-800 rounded-2xl p-5" data-testid={testid}>
      <h2 className="font-display text-lg font-bold mb-3">{title}</h2>
      <div className="space-y-2">
        {entries.length === 0 ? <p className="text-sm text-zinc-500">—</p> : entries.map(([k, n]) => (
          <div key={k} className="flex items-center justify-between text-sm">
            <span className="text-zinc-300">{k}</span><span className="font-mono text-amber-400">{n}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-amber-500 animate-spin" /></div>;
  const u = d.users;
  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <h1 className="font-display text-3xl font-extrabold">Tableau de bord</h1>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Utilisateurs" value={u.total} icon={Users} testid="stat-users" />
        <Stat label="Artistes" value={u.artists} icon={Users} testid="stat-artists" />
        <Stat label="Managers" value={u.managers} icon={UserCheck} testid="stat-managers" />
        <Stat label="Actifs (14j)" value={u.active} icon={UserCheck} testid="stat-active" />
        <Stat label="Nouveaux (7j)" value={u.new_7d} icon={Users} testid="stat-new" />
        <Stat label="Désactivés" value={u.disabled} icon={Users} testid="stat-disabled" />
        <Stat label="Projets" value={d.projects.total} icon={Briefcase} testid="stat-projects" />
        <Stat label="XP total" value={d.xp_total} icon={Zap} testid="stat-xp" />
        <Stat label="Visuels IA" value={d.images_total} icon={ImageIcon} testid="stat-images" />
        <Stat label="Relations mgr" value={d.relationships_accepted} icon={UserCheck} testid="stat-rels" />
        <Stat label="Invit. en attente" value={d.invites_pending} icon={Mail} testid="stat-invites" />
        <Stat label="Tâches" value={d.tasks_total} icon={Briefcase} testid="stat-tasks" />
        <Stat label="Vérifiés" value={u.verified} icon={ShieldCheck} testid="stat-verified" />
        <Stat label="En attente" value={u.pending} icon={Mail} testid="stat-pending" />
        <Stat label="Nouveaux (30j)" value={u.new_30d} icon={Users} testid="stat-new30" />
        <Stat label="Professionnels" value={d.marketplace?.pros ?? 0} icon={Store} testid="stat-pros" />
        <Stat label="Demandes" value={d.marketplace?.requests_total ?? 0} icon={FileText} testid="stat-requests" />
        <Stat label="Propositions" value={d.marketplace?.proposals_total ?? 0} icon={FileText} testid="stat-proposals" />
        <Stat label="Conversations" value={d.messaging?.conversations ?? 0} icon={MessageSquare} testid="stat-convs" />
        <Stat label="Messages" value={d.messaging?.messages ?? 0} icon={MessageSquare} testid="stat-msgs" />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Breakdown title="Par type de profil" data={d.profile_types} testid="admin-ptypes" />
        <Breakdown title="Par domaine" data={d.domains} testid="admin-domains" />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-[#18181E] border border-zinc-800 rounded-2xl p-5" data-testid="admin-plans-breakdown">
          <h2 className="font-display text-lg font-bold mb-3">Répartition des offres</h2>
          <div className="space-y-2">
            {Object.entries(d.plans).map(([p, n]) => (
              <div key={p} className="flex items-center justify-between text-sm">
                <span className="text-zinc-300">{p}</span>
                <span className="font-mono text-amber-400">{n}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-[#18181E] border border-zinc-800 rounded-2xl p-5" data-testid="admin-recent">
          <h2 className="font-display text-lg font-bold mb-3">Inscriptions récentes</h2>
          <div className="space-y-2">
            {d.recent_signups.map((r, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <span className="text-zinc-300 truncate">{r.artist_name} · {r.email}</span>
                <span className="text-[10px] text-zinc-500">{(r.roles || []).join("+")}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
